import { Rarity as RarityEnum } from '@/gql/type';
import React from 'react';
import styled from 'styled-components';

export interface RarityBadgeProps {
    rarity?: string | number | null;
    className?: string;
}

function normalizeRarity(
    rarity?: string | number | null,
): keyof typeof labelColorMap {
    if (rarity === null || rarity === undefined) return 'Common';

    // Numeric mapping (0..n)
    if (typeof rarity === 'number') {
        const mapByIndex: Array<keyof typeof labelColorMap> = [
            'Common',
            'Advanced',
            'Rare',
            'Epic',
        ];
        return mapByIndex[rarity] ?? 'Common';
    }

    // String mapping (case-insensitive)
    const lower = rarity.toString().toLowerCase();
    if (lower in rarityAliasMap) return rarityAliasMap[lower];

    // Fallback to exact enum if matches
    if ((Object.values(RarityEnum) as string[]).includes(rarity as string)) {
        return rarity as keyof typeof labelColorMap;
    }

    return 'Common';
}

const rarityAliasMap: Record<string, keyof typeof labelColorMap> = {
    // Common synonyms
    common: 'Common',

    // Advanced synonyms
    advanced: 'Advanced',
    uncommon: 'Advanced',

    // Rare synonyms
    rare: 'Rare',

    // Epic/Lengendary synonyms
    epic: 'Epic',
    legendary: 'Epic',
};

const labelColorMap = {
    Common: { label: 'Common', color: 'rgba(57, 182, 191, 1)' },
    Advanced: { label: 'Advanced', color: 'rgba(44, 68, 176, 1)' },
    Rare: { label: 'Rare', color: 'rgba(105, 0, 210, 1)' },
    Epic: { label: 'Legendary', color: 'rgba(252, 113, 134, 1)' },
};

const Badge = styled.div<{ $color: string }>`
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;

    color: white;
    text-transform: capitalize;
    background: ${(props) => props.$color};
`;

export const RarityBadge: React.FC<RarityBadgeProps> = ({
    rarity,
    className,
}) => {
    const key = normalizeRarity(rarity);
    const { label, color } = labelColorMap[key];
    return (
        <Badge className={className} $color={color}>
            {label}
        </Badge>
    );
};

export default RarityBadge;
